import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EyeIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormAssignModal from './FormAssignModal'
import FormResponseViewModal from './FormResponseViewModal'
import FormPreviewModal from './FormPreviewModal'
import { assignmentHasResponse } from './formResponseHelpers'

import { formatSydneyDate } from '../../utils/sydneyDateTime'
const RETURNED = new Set(['submitted', 'accepted', 'in_review'])

function statusMeta(status) {
  if (RETURNED.has(status)) return { label: status === 'in_review' ? 'Returned — in review' : 'Filled & returned', tone: 'green' }
  if (status === 'overdue') return { label: 'Overdue', tone: 'red' }
  if (status === 'opened' || status === 'in_progress') return { label: 'Opened', tone: 'amber' }
  if (status === 'sent' || status === 'assigned') return { label: status === 'sent' ? 'Sent' : 'Assigned', tone: 'slate' }
  if (status === 'cancelled') return { label: 'Cancelled', tone: 'slate' }
  return { label: status || '—', tone: 'slate' }
}

const TONE = {
  green: { color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
  amber: { color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
  red: { color: '#991b1b', bg: '#fef2f2', border: '#fecaca' },
  slate: { color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
}

function mergeAssignments(lists) {
  const seen = new Set()
  const out = []
  for (const list of lists) {
    for (const row of list || []) {
      if (!row?.id || seen.has(row.id)) continue
      seen.add(row.id)
      out.push(row)
    }
  }
  return out.sort((a, b) => String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || '')))
}

async function fetchAssignments(params) {
  const qs = new URLSearchParams(params)
  const res = await authFetch(getLocalApiUrl(`/api/forms/assignments?${qs}`))
  const j = await res.json()
  if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load forms')
  return j.data || []
}

export default function RecordFormsModal({
  open,
  onClose,
  subjectType,
  subjectId,
  subjectLabel = '',
  patronId = '',
  emailTo: emailToProp = '',
  smsTo: smsToProp = '',
  placement = '',
  readOnly = false,
  title = 'Forms',
  description = '',
  onAssignmentsChange,
  onAssigned,
}) {
  const navigate = useNavigate()
  const [assignments, setAssignments] = useState([])
  const [catalog, setCatalog] = useState([])
  const [loading, setLoading] = useState(true)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [error, setError] = useState('')
  const [formFilter, setFormFilter] = useState('')
  const [activeForm, setActiveForm] = useState(null)
  const [previewForm, setPreviewForm] = useState(null)
  const [viewAssignmentId, setViewAssignmentId] = useState(null)
  const [contact, setContact] = useState(null)
  const onAssignmentsChangeRef = useRef(onAssignmentsChange)
  onAssignmentsChangeRef.current = onAssignmentsChange

  const notifyAssignmentsChange = useCallback(() => {
    onAssignmentsChangeRef.current?.()
  }, [])

  const loadAssignments = useCallback(async () => {
    if (!subjectId && !patronId) return
    setLoading(true)
    setError('')
    try {
      const queries = []
      if (subjectType && subjectId) queries.push(fetchAssignments({ subjectType, subjectId }))
      if (patronId && String(patronId) !== String(subjectId)) {
        queries.push(fetchAssignments({ subjectType: 'patron', subjectId: patronId }))
      }
      const lists = await Promise.all(queries)
      setAssignments(mergeAssignments(lists))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [subjectType, subjectId, patronId])

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true)
    try {
      const qs = placement ? `?status=published&placement=${encodeURIComponent(placement)}` : '?status=published'
      const res = await authFetch(getLocalApiUrl(`/api/forms${qs}`))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load published forms')
      setCatalog(j.data || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setCatalogLoading(false)
    }
  }, [placement])

  useEffect(() => {
    if (!open) return undefined
    setFormFilter('')
    setActiveForm(null)
    setPreviewForm(null)
    setViewAssignmentId(null)
    return undefined
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    loadAssignments()
    loadCatalog()
    return undefined
  }, [open, loadAssignments, loadCatalog])

  useEffect(() => {
    if (!open || subjectType !== 'ecdd' || !subjectId) {
      setContact(null)
      return undefined
    }
    let cancelled = false
    authFetch(getLocalApiUrl(`/api/ecdd/${encodeURIComponent(subjectId)}/outreach/contact`))
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled && j.success) setContact(j.data)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [open, subjectType, subjectId])

  const visibleForms = useMemo(() => {
    const q = formFilter.trim().toLowerCase()
    return (catalog || []).filter((f) => !q || String(f.name || '').toLowerCase().includes(q))
  }, [catalog, formFilter])

  const patronForms = visibleForms.filter((f) => f.facing === 'patron')
  const internalForms = visibleForms.filter((f) => f.facing !== 'patron')

  const preset = {
    subjectType: contact?.masterProfileId || patronId
      ? 'patron'
      : (subjectType || (patronId ? 'patron' : 'none')),
    subjectId: contact?.masterProfileId || patronId || subjectId || '',
    subjectLabel: contact?.patronName || subjectLabel || 'This record',
    emailTo: emailToProp || contact?.email || '',
    smsTo: smsToProp || contact?.phone || '',
  }

  const returnedCount = assignments.filter((a) => RETURNED.has(a.status)).length
  const cancelledCount = assignments.filter((a) => a.status === 'cancelled').length
  const openCount = assignments.filter((a) => !RETURNED.has(a.status) && a.status !== 'cancelled').length

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
        <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-slate-200">
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Record forms</div>
              <h2 className="text-lg font-bold text-[#073B4D] m-0 mt-0.5 truncate">{title}</h2>
              <p className="text-xs text-slate-500 m-0 mt-1">
                {description || 'Preview a form, send it to the patron or staff, and track what comes back.'}
              </p>
              {!loading && assignments.length ? (
                <p className="text-[11px] text-teal-800 m-0 mt-1.5 font-medium">
                  {returnedCount} returned · {openCount} still out{cancelledCount ? ` · ${cancelledCount} cancelled` : ''}
                </p>
              ) : null}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0" aria-label="Close">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div> : null}

            <section>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">On this record</div>
              {loading ? <div className="text-xs text-slate-500">Loading forms…</div> : null}
              {!loading && assignments.length === 0 ? (
                <div className="text-xs text-slate-500 rounded-lg border border-dashed border-slate-200 px-3 py-3">
                  No forms sent on this record yet.
                </div>
              ) : null}
              <div className="space-y-1.5">
                {assignments.map((a) => {
                  const meta = statusMeta(a.status)
                  const tone = TONE[meta.tone]
                  const hasResponse = assignmentHasResponse(a)
                  return (
                    <div
                      key={a.id}
                      className="rounded-lg border px-3 py-2.5"
                      style={{ borderColor: tone.border, background: tone.bg }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/compliance-safe/forms/assignments/${a.id}`)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="text-sm font-semibold text-slate-900 truncate">{a.formName}</div>
                          <div className="text-[11px] text-slate-500">
                            <FacingBadge facing={a.facing} />
                            {a.submittedAt ? ` · back ${formatSydneyDate(new Date(a.submittedAt))}` : a.dueAt ? ` · due ${formatSydneyDate(new Date(a.dueAt))}` : ''}
                            {a.submissionRef ? ` · ${a.submissionRef}` : ''}
                          </div>
                        </button>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: tone.color }}>
                            {meta.label}
                          </span>
                          {hasResponse ? (
                            <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => setViewAssignmentId(a.id)}>
                              View response
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>

            {!readOnly ? (
              <section>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Send a form</div>
                <input
                  className="mb-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder="Find a published form…"
                  value={formFilter}
                  onChange={(e) => setFormFilter(e.target.value)}
                />
                {catalogLoading ? <div className="text-xs text-slate-500">Loading published forms…</div> : null}
                {!catalogLoading && !error && visibleForms.length === 0 ? (
                  <div className="text-xs text-slate-500 rounded-lg border border-dashed border-slate-200 px-3 py-3">
                    No published forms{placement ? ' for this placement' : ''}.
                    {placement === 'profile.extra' ? (
                      <span className="block mt-1 text-slate-500 font-normal">
                        In Form Builder, tick <strong>Profile — Additional forms</strong> on the form, then publish. ECDD can list every published form; this list only shows forms with that Profile placement.
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {!error ? (
                  <div className="space-y-3">
                    <FormGroup
                      heading="Patron forms"
                      hint="Send to the patron — they fill and return"
                      empty="No patron forms published."
                      forms={patronForms}
                      onPreview={setPreviewForm}
                      onSend={setActiveForm}
                      kind="patron"
                    />
                    <FormGroup
                      heading="Internal forms"
                      hint="Staff fill — creates a Governance task"
                      empty="No internal forms published."
                      forms={internalForms}
                      onPreview={setPreviewForm}
                      onSend={setActiveForm}
                      kind="internal"
                    />
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>

          <div className="px-5 py-3 border-t border-slate-100 flex justify-end shrink-0 bg-slate-50/80 rounded-b-xl">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>

      {activeForm ? (
        <FormAssignModal
          form={activeForm}
          presetSubject={preset}
          onClose={() => setActiveForm(null)}
          onDone={(assignment) => {
            const form = activeForm
            setActiveForm(null)
            loadAssignments()
            notifyAssignmentsChange()
            onAssigned?.(assignment, form)
          }}
        />
      ) : null}
      <FormResponseViewModal
        assignmentId={viewAssignmentId}
        open={!!viewAssignmentId}
        onClose={() => setViewAssignmentId(null)}
      />
      <FormPreviewModal
        formId={previewForm?.id}
        formName={previewForm?.name}
        open={!!previewForm}
        onClose={() => setPreviewForm(null)}
      />
    </>
  )
}

function FacingBadge({ facing }) {
  const patron = facing === 'patron'
  return (
    <span
      className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={patron
        ? { color: '#0f766e', background: '#ccfbf1' }
        : { color: '#334155', background: '#e2e8f0' }}
    >
      {patron ? 'Patron' : 'Internal'}
    </span>
  )
}

function FormGroup({ heading, hint, empty, forms, onPreview, onSend, kind }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <FacingBadge facing={kind === 'patron' ? 'patron' : 'internal'} />
        <div>
          <div className="text-[11px] font-bold text-slate-800">{heading}</div>
          <div className="text-[10px] text-slate-500">{hint}</div>
        </div>
      </div>
      {forms.length === 0 ? (
        <div className="px-2 py-1.5 text-[11px] text-slate-400">{empty}</div>
      ) : forms.map((f) => (
        <div
          key={f.id}
          className="flex items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 mb-1.5"
          style={{ borderColor: kind === 'patron' ? '#99f6e4' : '#e2e8f0' }}
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{f.name}</div>
            <div className="text-[11px] text-slate-500">
              {kind === 'patron' ? 'Email / SMS to the patron' : 'Assigned to staff on this record'}
            </div>
          </div>
          <div className="flex flex-wrap gap-1 shrink-0">
            <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => onPreview(f)}>
              <EyeIcon className="h-3.5 w-3.5 mr-1" />
              Preview
            </Button>
            <Button type="button" size="sm" className="h-8 text-xs" onClick={() => onSend(f)}>
              {kind === 'patron' ? 'Send' : 'Assign'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}
